import {CSSProperties} from "react";
import {IServiceFactory} from "../service-factory.interface";
import {Spine, IPixiAppCreateOptions, Application, LoaderResource} from "./pixi-app.service.interface";

export interface IPixiSpineResourceDef {
    name: string;
    jsonUrl: string;
}

type AnimationDef = (stuff: {pixiApp: Application, spineAnimations: Record<string, Spine>; loaderResources: Record<string, LoaderResource>;}) => (void | (() => void));

export interface IAnimationRenderer {
    render: AnimationDef;
    dispose?(): void
}

export enum AnimationStatus {
    Idle,
    Loading,
    Failed,
    Aborted,
    Running,
    Disposed
}

interface IPixiAnimationPromiseResultSuccess {
    status: AnimationStatus.Running;
    canvas: HTMLCanvasElement;
}
interface IPixiAnimationPromiseResultOther {
    status: AnimationStatus.Failed | AnimationStatus.Aborted | AnimationStatus.Disposed;
    canvas?: undefined;
}

type IPixiAnimationPromiseResult = IPixiAnimationPromiseResultSuccess | IPixiAnimationPromiseResultOther;

export class PixiSpineAnimation {
    private pixiApp: Application;
    private pixiAppDisposer: () => void;
    private _spineAnimations: Record<string, Spine> = {};
    private _loaderResources: Record<string, LoaderResource> = {};
    private disposeActions: Array<() => void> = [];
    private _signalAborted = false;
    private _status: AnimationStatus = AnimationStatus.Idle;

    constructor(
        private services: IServiceFactory,
        private pixiAppCreateOptions: IPixiAppCreateOptions,
        private resourcesUrls: IPixiSpineResourceDef[],
        private renderer: IAnimationRenderer,
        private canvasCss?: CSSProperties
    ) {
    }

    private initPromise: Promise<IPixiAnimationPromiseResult> = null;
    public start(): Promise<IPixiAnimationPromiseResult> {
        if (!this.initPromise) {
            this.initPromise = this._initialize();
        }
        return this.initPromise;
    }

    private async _initialize(): Promise<IPixiAnimationPromiseResult> {
        // The init started, set the status as Loading
        this._status = AnimationStatus.Loading;
        // between constructor and start, the animation could have been aborted or disposed, check it
        if (this._signalAborted) {
            this._status = AnimationStatus.Aborted;
            return {status: AnimationStatus.Aborted};
        }

        // create pixi app. this loads the pixi and pixi-spine libraries first time they are called
        let {app: pixiApp, dispose: disposePixiApp, PixiSpine, canvas} = await this.services.pixiApp.createPixiApp(this.pixiAppCreateOptions);
        this.pixiApp = pixiApp;
        this.pixiAppDisposer = disposePixiApp;

        const {resources} = await this.services.pixiApp.loadResources(pixiApp, this.resourcesUrls).catch((e: unknown) => {
            this._status = AnimationStatus.Failed;
            disposePixiApp();
            throw e;
        });

        // loading of pixi libraries and animation resources are async, I must check if someone aborted the animation meanwhile
        if (this._signalAborted) {
            this._status = AnimationStatus.Aborted;
            this._disposeInternal();
            return {status: AnimationStatus.Aborted};
        }

        // Apply custom CSS to the canvas element created by pixi
        if (this.canvasCss) {
            Object.getOwnPropertyNames(this.canvasCss).forEach((styleKey) => {
                canvas.style[styleKey] = this.canvasCss[styleKey];
            });
        }

        // for all loaded resources instantiate Spine objects.
        // keep both raw loaded resources and Spine objects in Map or Record objects. key is the name.
        this.resourcesUrls.forEach((resUrl) => {
            this._loaderResources[resUrl.name] = resources[resUrl.name];
            let spineAnimation = new PixiSpine.Spine(resources[resUrl.name].spineData);
            this._spineAnimations[resUrl.name] = spineAnimation;
            pixiApp.stage.addChild(spineAnimation);
        });

        // run the specific animation instructions that are given in constructor
        // for now, only sync code is supported. to support async make it await
        // const renderDisposer = await this.renderer.render({spineAnimations, loaderResources}); // <- the renderer return Promise<RenderDisposer | void>
        const renderDisposer = this.renderer.render({pixiApp, spineAnimations: this._spineAnimations, loaderResources: this._loaderResources});
        if (renderDisposer) {
            this.disposeActions.push(renderDisposer);
        }

        // in case the renderer is async then we should check again if the loading was aborted.
        // for now, this code is useless because for the renderer to support async code it should be something like this:
        // const renderDisposer = await this.renderer.render({spineAnimations, loaderResources}); // <- the renderer return Promise<RenderDisposer | void>
        if (this._signalAborted) {
            this._status = AnimationStatus.Aborted;
            this._disposeInternal();
            return {status: AnimationStatus.Aborted};
        }

        // all done, set the status to Running and return the status (resolve)
        this._status = AnimationStatus.Running;

        return {status: AnimationStatus.Running, canvas: this.pixiApp.view};
    }

    public attachToDOM(wrapper: HTMLElement): Promise<IPixiAnimationPromiseResult> {
        if (!this.initPromise) {
            throw new Error("Method attachToDOM MUST be called after the method start");
        }
        return this.initPromise.then((result) => {
            // At this point the status can be only Aborted or Running
            // Or, after disposal, the status could be Disposed
            if (result.status === AnimationStatus.Running) {
                wrapper.appendChild(result.canvas);
            }
            return result;
        });
    }

    public get spineAnimations() {
        return this._spineAnimations;
    }

    public get loaderResources() {
        return this._loaderResources;
    }

    public get status() {
        return this._status;
    }

    /**
     * Dispose is the one method to be called both for disposal of the running animation and for aborting a loading one
     * If the animation object is newly created or loading, then the controller abort will be called.
     * that will cause a call to _disposeInternal from the _initialize method, as soon as the method checks the aborted flag
     * If the animation is running, we set the disposed status and call _disposeInternal owrselves
     */
    public dispose = () => {
        if (this._status === AnimationStatus.Idle || this._status === AnimationStatus.Loading) {
            this._signalAborted = true;
        } else if (this._status === AnimationStatus.Running) {
            this._status = AnimationStatus.Disposed;
            this._disposeInternal();
        }
    }

    private _disposeInternal = () => {
        while (this.disposeActions.length) {
            const disposeAction = this.disposeActions.pop();
            disposeAction();
        };
        if (this.renderer.dispose) {
            this.renderer.dispose();
        }
        while (this.resourcesUrls.length) {
            const resource = this.resourcesUrls.pop();
            if (this._spineAnimations[resource.name]) {
                this._spineAnimations[resource.name].autoUpdate = false;
                this._spineAnimations[resource.name].destroy();
                delete this._spineAnimations[resource.name];
            }
        }
        this.pixiAppDisposer();
    }

}
